/**
 * Maps human-readable keys to translation IDs for the compare-health-check-ups-switzerland landing page.
 * This is new content (not from Framer XLIFF), so keys use the htc_ prefix directly.
 */
export const compareHealthCheckUpsKeys = {
  // Hero
  pageLabel: "htc_pageLabel", // "PREVENTIVE CHECK-UPS"
  heading: "htc_heading", // "How to choose a preventive health check-up in Switzerland"
  subtitle: "htc_subtitle", // "Making an informed decision..."
  compareOptions: "htc_compareOptions", // "See the comparison"
  bookCheckup: "htc_bookCheckup", // "Book a check-up"
  heroBullet1: "htc_heroBullet1", // "Coverage, protocols & blood panels explained"
  heroBullet2: "htc_heroBullet2", // "A checklist to help you choose"
  heroBullet3: "htc_heroBullet3", // "Side-by-side comparison of three check-up types"

  // Why this matters
  whyLabel: "htc_whyLabel", // "WHY THIS MATTERS"
  whyHeading: "htc_whyHeading", // "Early detection starts with the right questions"
  whyP1: "htc_whyP1",
  whyP2: "htc_whyP2",
  whyP3: "htc_whyP3",
  trustSubtitle: "htc_trustSubtitle", // "University-affiliated radiologists, Harvard-trained prevention lead, AI imaging specialist from TU Munich."
  whyAsksLabel: "htc_whyAsksLabel", // "THE STANDARD QUESTION"
  whyWantsLabel: "htc_whyWantsLabel", // "THE QUESTION YOU'RE ASKING"
  whyMattersLabel: "htc_whyMattersLabel", // "WHY IT MATTERS"
  whyAsksQuestion: "htc_whyAsksQuestion", // "Is there a problem right now?"
  whyWantsQuestion: "htc_whyWantsQuestion", // "What direction is my health heading?"
  whyAsksBody: "htc_whyAsksBody", // "A standard Swiss check-up is built to answer this — and little else."
  whyWantsBody: "htc_whyWantsBody", // "A different question requires a different exam — more biomarkers, more organs, expert eyes on the images."
  whyStat1Value: "htc_whyStat1Value", // "90%"
  whyStat1Text: "htc_whyStat1Text",
  whyStat1Source: "htc_whyStat1Source",
  whyStat2Value: "htc_whyStat2Value", // "1 in 5"
  whyStat2Text: "htc_whyStat2Text",
  whyStat2Source: "htc_whyStat2Source",
  whyStat3Value: "htc_whyStat3Value", // "15–20"
  whyStat3Text: "htc_whyStat3Text",
  whyPortraitAlt: "htc_whyPortraitAlt",

  // Who is this for
  whoLabel: "htc_whoLabel", // "Who is this for"
  whoHeading: "htc_whoHeading", // "A comprehensive check-up is designed for people who…"
  whoCard1Title: "htc_whoCard1Title", // "Want a proactive baseline"
  whoCard1Body: "htc_whoCard1Body",
  whoCard1Alt: "htc_whoCard1Alt",
  whoCard2Title: "htc_whoCard2Title", // "Have a family history"
  whoCard2Body: "htc_whoCard2Body",
  whoCard2Alt: "htc_whoCard2Alt",
  whoCard3Title: "htc_whoCard3Title", // "Have outgrown the GP check-up"
  whoCard3Body: "htc_whoCard3Body",
  whoCard3Alt: "htc_whoCard3Alt",
  whoCard4Title: "htc_whoCard4Title", // "Want to track over time"
  whoCard4Body: "htc_whoCard4Body",
  whoCard4Alt: "htc_whoCard4Alt",

  // At-a-glance comparison
  glanceLabel: "htc_glanceLabel", // "Side-by-side comparison"
  glanceHeading: "htc_glanceHeading", // "How preventive check-ups compare at a glance"
  glanceColAhead: "htc_glanceColAhead", // "Ahead Health"
  glanceColTypical: "htc_glanceColTypical", // "Typical provider"
  glanceColGp: "htc_glanceColGp", // "Swiss GP check-up *"
  glanceRow1Crit: "htc_glanceRow1Crit",
  glanceRow1Ahead: "htc_glanceRow1Ahead",
  glanceRow1Typical: "htc_glanceRow1Typical",
  glanceRow1Gp: "htc_glanceRow1Gp",
  glanceRow2Crit: "htc_glanceRow2Crit",
  glanceRow2Typical: "htc_glanceRow2Typical",
  glanceRow3Crit: "htc_glanceRow3Crit",
  glanceRow3Ahead: "htc_glanceRow3Ahead",
  glanceRow3Typical: "htc_glanceRow3Typical",
  glanceRow4Crit: "htc_glanceRow4Crit",
  glanceRow4Ahead: "htc_glanceRow4Ahead",
  glanceRow4Typical: "htc_glanceRow4Typical",
  glanceRow5Crit: "htc_glanceRow5Crit",
  glanceRow5Ahead: "htc_glanceRow5Ahead",
  glanceRow5Typical: "htc_glanceRow5Typical",
  glanceRow5Gp: "htc_glanceRow5Gp",
  glanceRow6Crit: "htc_glanceRow6Crit",
  glanceRow6Ahead: "htc_glanceRow6Ahead",
  glanceRow6Typical: "htc_glanceRow6Typical",
  glanceRow6Gp: "htc_glanceRow6Gp",
  glanceRow7Crit: "htc_glanceRow7Crit",
  glanceRow7Ahead: "htc_glanceRow7Ahead",
  glanceRow7Typical: "htc_glanceRow7Typical",
  glanceRow7Gp: "htc_glanceRow7Gp",
  glanceRow8Crit: "htc_glanceRow8Crit",
  glanceRow8Ahead: "htc_glanceRow8Ahead",
  glanceRow8Typical: "htc_glanceRow8Typical",
  glanceRow8Gp: "htc_glanceRow8Gp",
  glanceRow9Crit: "htc_glanceRow9Crit",
  glanceRow9Typical: "htc_glanceRow9Typical",
  glanceRow9Gp: "htc_glanceRow9Gp",
  glanceNote: "htc_glanceNote",

  // Blood biomarker comparison (placed below at-a-glance)
  bloodLabel: "htc_bloodLabel", // "BLOOD ANALYSIS"
  bloodHeading: "htc_bloodHeading", // "Blood analysis: why the number of biomarkers matters"
  bloodSubtitle: "htc_bloodSubtitle", // long paragraph
  bloodColAhead: "htc_bloodColAhead", // "Ahead Health (Advanced, 81 biomarkers)"
  bloodColGp: "htc_bloodColGp", // "Standard Swiss GP check-up (15–20 markers)"
  bloodRow1Crit: "htc_bloodRow1Crit", // Standard lipids (...)
  bloodRow2Crit: "htc_bloodRow2Crit", // ApoB
  bloodRow3Crit: "htc_bloodRow3Crit", // Lp(a)
  bloodRow3Ahead: "htc_bloodRow3Ahead", // Add-on
  bloodRow4Crit: "htc_bloodRow4Crit", // Fasting glucose
  bloodRow5Crit: "htc_bloodRow5Crit", // HbA1c
  bloodRow5Gp: "htc_bloodRow5Gp", // Sometimes (if diabetes risk)
  bloodRow6Crit: "htc_bloodRow6Crit", // Fasting insulin
  bloodRow7Crit: "htc_bloodRow7Crit", // Liver enzymes (ALT, AST, GGT)
  bloodRow8Crit: "htc_bloodRow8Crit", // Kidney function (creatinine, eGFR)
  bloodRow9Crit: "htc_bloodRow9Crit", // Full thyroid panel
  bloodRow9Gp: "htc_bloodRow9Gp", // TSH only (if indicated)
  bloodRow10Crit: "htc_bloodRow10Crit", // Iron studies
  bloodRow10Gp: "htc_bloodRow10Gp", // ✗ (unless symptomatic)
  bloodRow11Crit: "htc_bloodRow11Crit", // Vitamin D
  bloodRow12Crit: "htc_bloodRow12Crit", // Vitamin B12 & folate (B9)
  bloodRow12Ahead: "htc_bloodRow12Ahead", // Vitamin B blood test add-on
  bloodRow13Crit: "htc_bloodRow13Crit", // hs-CRP (inflammation)
  bloodRow14Crit: "htc_bloodRow14Crit", // Full blood count
  bloodRow15Crit: "htc_bloodRow15Crit", // Hormones (testosterone, oestradiol, DHEA-S)
  bloodRow15Ahead: "htc_bloodRow15Ahead", // Hormone blood test add-on
  bloodRow16Crit: "htc_bloodRow16Crit", // Gender-specific markers (PSA)
  bloodRow16Ahead: "htc_bloodRow16Ahead", // "Add-on for everyone ({price}), included in Pro"
  bloodRow16Gp: "htc_bloodRow16Gp", // PSA only if indicated, age 50+
  bloodRow17Crit: "htc_bloodRow17Crit", // Integrated with MRI findings in one report
  bloodRow17Gp: "htc_bloodRow17Gp", // N/A

  // Six factors
  factorsHeading: "htc_factorsHeading",
  factor1Title: "htc_factor1Title",
  factor1Body: "htc_factor1Body",
  factor2Title: "htc_factor2Title",
  factor2Body: "htc_factor2Body",
  factor3Title: "htc_factor3Title",
  factor3Body: "htc_factor3Body",
  factor4Title: "htc_factor4Title",
  factor4Body: "htc_factor4Body",
  factor5Title: "htc_factor5Title",
  factor5Body: "htc_factor5Body",
  factor6Title: "htc_factor6Title",
  factor6Body: "htc_factor6Body",

  // Why Ahead Health
  whyAhTitle: "htc_whyAhTitle", // "Why Ahead Health"
  whyAhStat: "htc_whyAhStat", // "30,000+ MRIs reviewed"
  whyAhBody: "htc_whyAhBody",
  whyAhPartners: "htc_whyAhPartners",
  whyLocTitle: "htc_whyLocTitle", // "Locations"
  whyLocBody: "htc_whyLocBody",
  whyLocSeeAll: "htc_whyLocSeeAll", // "See all locations"
  whyLocAll: "htc_whyLocAll", // "All locations"
  whyLocCityList: "htc_whyLocCityList", // comma-separated, localized city names
  whyLoc1Name: "htc_whyLoc1Name",
  whyLoc1Addr: "htc_whyLoc1Addr",
  whyLoc2Name: "htc_whyLoc2Name",
  whyLoc2Addr: "htc_whyLoc2Addr",
  whyPortraitManAlt: "htc_whyPortraitManAlt",
  whyReportTitle: "htc_whyReportTitle", // "One integrated report"
  whyReportBody: "htc_whyReportBody",
  whyReportHeartValue: "htc_whyReportHeartValue",
  whyReportHeartLabel: "htc_whyReportHeartLabel",
  whyReportHeartNote: "htc_whyReportHeartNote",
  whyReportAgeValue: "htc_whyReportAgeValue",
  whyReportAgeLabel: "htc_whyReportAgeLabel",
  whyReportAgeNote: "htc_whyReportAgeNote",
  whyReportFindingsValue: "htc_whyReportFindingsValue",
  whyReportFindingsLabel: "htc_whyReportFindingsLabel",
  whyReportBiomarkersValue: "htc_whyReportBiomarkersValue",
  whyReportBiomarkersLabel: "htc_whyReportBiomarkersLabel",

  // Tabs
  tabMri: "htc_tabMri", // "Full-body MRI"
  tabBlood: "htc_tabBlood", // "Blood analysis"

  // MRI comparison column headers
  mriColAhead: "htc_mriColAhead", // "Ahead Health"
  mriColImaging: "htc_mriColImaging", // "Typical imaging center"
  mriColGp: "htc_mriColGp", // "GP check-up"

  // MRI rows — criterion, ahead value, detail
  mriProtocol: "htc_mriProtocol", // "Scan protocol"
  mriProtocolAhead: "htc_mriProtocolAhead", // "Research-grade"
  mriProtocolDetail: "htc_mriProtocolDetail",
  mriCoverage: "htc_mriCoverage",
  mriCoverageAhead: "htc_mriCoverageAhead", // "12 organ systems"
  mriCoverageDetail: "htc_mriCoverageDetail",
  mriDuration: "htc_mriDuration",
  mriDurationAhead: "htc_mriDurationAhead", // "60 min"
  mriDurationDetail: "htc_mriDurationDetail",
  mriAi: "htc_mriAi",
  mriAiAhead: "htc_mriAiAhead", // "AI-assisted"
  mriAiDetail: "htc_mriAiDetail",
  mriReport: "htc_mriReport",
  mriReportAhead: "htc_mriReportAhead", // "Actionable health plan"
  mriReportDetail: "htc_mriReportDetail",
  mriFollowup: "htc_mriFollowup",
  mriFollowupAhead: "htc_mriFollowupAhead", // "Included"
  mriFollowupDetail: "htc_mriFollowupDetail",
  mriData: "htc_mriData",
  mriDataAhead: "htc_mriDataAhead", // "Digital vault"
  mriDataDetail: "htc_mriDataDetail",
  mriReaders: "htc_mriReaders",
  mriReadersAhead: "htc_mriReadersAhead", // "University professors"
  mriReadersDetail: "htc_mriReadersDetail",

  // Blood rows (legacy, used by the markdown mirror only)
  bloodMarkers: "htc_bloodMarkers",
  bloodMarkersAhead: "htc_bloodMarkersAhead", // "81 biomarkers"
  bloodMarkersDetail: "htc_bloodMarkersDetail",
  bloodHormones: "htc_bloodHormones",
  bloodHormonesAhead: "htc_bloodHormonesAhead",
  bloodHormonesDetail: "htc_bloodHormonesDetail",
  bloodInflammation: "htc_bloodInflammation",
  bloodInflammationAhead: "htc_bloodInflammationAhead",
  bloodInflammationDetail: "htc_bloodInflammationDetail",
  bloodVitamins: "htc_bloodVitamins",
  bloodVitaminsAhead: "htc_bloodVitaminsAhead",
  bloodVitaminsDetail: "htc_bloodVitaminsDetail",
  bloodCardio: "htc_bloodCardio",
  bloodCardioAhead: "htc_bloodCardioAhead",
  bloodCardioDetail: "htc_bloodCardioDetail",
  bloodTrend: "htc_bloodTrend",
  bloodTrendAhead: "htc_bloodTrendAhead",
  bloodTrendDetail: "htc_bloodTrendDetail",

  // Section headings
  combinedHeading: "htc_combinedHeading", // "Two data sources, one complete picture"
  combinedSubtitle: "htc_combinedSubtitle",
  checklistHeading: "htc_checklistHeading", // "16 questions to ask any provider"
  reportHeading: "htc_reportHeading", // "What you get back"
  reportSubtitle: "htc_reportSubtitle",
  compareLabel: "htc_compareLabel",
  compareHeading: "htc_compareHeading",
  compareAheadTitle: "htc_compareAheadTitle",
  compareAheadItem1: "htc_compareAheadItem1",
  compareAheadItem2: "htc_compareAheadItem2",
  compareAheadItem3: "htc_compareAheadItem3",
  compareAheadItem4: "htc_compareAheadItem4",
  compareAheadItem5: "htc_compareAheadItem5",
  compareAheadItem6: "htc_compareAheadItem6",
  compareAheadCta: "htc_compareAheadCta",
  compareTypicalTitle: "htc_compareTypicalTitle",
  compareTypicalItem1: "htc_compareTypicalItem1",
  compareTypicalItem2: "htc_compareTypicalItem2",
  compareTypicalItem3: "htc_compareTypicalItem3",
  compareTypicalItem4: "htc_compareTypicalItem4",
  compareTypicalItem5: "htc_compareTypicalItem5",
  compareTypicalItem6: "htc_compareTypicalItem6",
  // Typical report mockup labels
  typicalReportTitle: "htc_typicalReportTitle",
  typicalReportPatientId: "htc_typicalReportPatientId",
  typicalReportSpecimenId: "htc_typicalReportSpecimenId",
  typicalReportDob: "htc_typicalReportDob",
  typicalReportPhysician: "htc_typicalReportPhysician",
  typicalReportAge: "htc_typicalReportAge",
  typicalReportSex: "htc_typicalReportSex",
  typicalReportDateCollected: "htc_typicalReportDateCollected",
  typicalReportDateReceived: "htc_typicalReportDateReceived",
  typicalReportDateReported: "htc_typicalReportDateReported",
  typicalReportPanel: "htc_typicalReportPanel",
  typicalReportUnit: "htc_typicalReportUnit",
  typicalReportRange: "htc_typicalReportRange",
  typicalReportIron: "htc_typicalReportIron",
  trustHeading: "htc_trustHeading", // "Who reads your scan"
  insuranceHeading: "htc_insuranceHeading", // "Insurance & cost"
  insuranceText: "htc_insuranceText",

  // Checklist
  checklistSubtitle: "htc_checklistSubtitle",
  checklistQ1: "htc_checklistQ1",
  checklistA1: "htc_checklistA1",
  checklistQ2: "htc_checklistQ2",
  checklistA2: "htc_checklistA2",
  checklistQ3: "htc_checklistQ3",
  checklistA3: "htc_checklistA3",
  checklistQ4: "htc_checklistQ4",
  checklistA4: "htc_checklistA4",
  checklistQ5: "htc_checklistQ5",
  checklistA5: "htc_checklistA5",
  checklistQ6: "htc_checklistQ6",
  checklistA6: "htc_checklistA6",
  checklistQ7: "htc_checklistQ7",
  checklistA7: "htc_checklistA7",
  checklistQ8: "htc_checklistQ8",
  checklistA8: "htc_checklistA8",
  checklistQ9: "htc_checklistQ9",
  checklistA9: "htc_checklistA9",
  checklistQ10: "htc_checklistQ10",
  checklistA10: "htc_checklistA10",
  checklistQ11: "htc_checklistQ11",
  checklistA11: "htc_checklistA11",
  checklistQ12: "htc_checklistQ12",
  checklistA12: "htc_checklistA12",
  checklistQ13: "htc_checklistQ13",
  checklistA13: "htc_checklistA13",
  checklistQ14: "htc_checklistQ14",
  checklistA14: "htc_checklistA14",
  checklistQ15: "htc_checklistQ15",
  checklistA15: "htc_checklistA15",
  checklistQ16: "htc_checklistQ16",
  checklistA16: "htc_checklistA16",

  // Report
  reportP1: "htc_reportP1",
  reportP2: "htc_reportP2",
  reportP3: "htc_reportP3",

  // Insurance buttons
  checkInsurance: "htc_checkInsurance", // "Check insurance coverage"
  seePricing: "htc_seePricing", // "See pricing"

  // Imaging + blood analysis combo section
  comboLabel: "htc_comboLabel", // "IMAGING + BLOOD ANALYSIS"
  comboHeading: "htc_comboHeading", // "Why the combination of imaging & blood analysis matters"
  comboQuote: "htc_comboQuote",
  comboAuthorRole: "htc_comboAuthorRole", // "Prevention Expert"
  comboCard1Title: "htc_comboCard1Title", // "Additional insights"
  comboCard1Desc: "htc_comboCard1Desc",
  comboCard1DoctorQuote: "htc_comboCard1DoctorQuote",
  comboCard1Actions: "htc_comboCard1Actions", // "2 actions"
  comboCard2Title: "htc_comboCard2Title", // "Clear signals"
  comboCard2Desc: "htc_comboCard2Desc",
  comboCard2Metric: "htc_comboCard2Metric", // "Fasting glucose"
  comboCard2Badge: "htc_comboCard2Badge", // "Normal range"
  comboCard2Note: "htc_comboCard2Note", // "No actions needed"
  comboCard3Title: "htc_comboCard3Title", // "Follow-up tests"
  comboCard3Desc: "htc_comboCard3Desc",
  comboCard3AddOns: "htc_comboCard3AddOns", // "+ 3 add-ons"
  comboCard3Status: "htc_comboCard3Status", // "Confirmed"
  comboCard3Date: "htc_comboCard3Date", // "October 25" (localized appointment date)
  comboCard3Directions: "htc_comboCard3Directions", // "Get directions"
  comboCard3Calendar: "htc_comboCard3Calendar", // "Add to calendar"
  comboCard4Title: "htc_comboCard4Title", // "Imaging insight"
  comboCard4Desc: "htc_comboCard4Desc", // "Track internal changes year over year."

  // Provider checklist (below insurance section)
  providerLabel: "htc_providerLabel", // "PROVIDER CHECKLIST"
  providerHeading: "htc_providerHeading", // "Questions to ask before you book"
  providerSubtitle: "htc_providerSubtitle",
  providerItem1Title: "htc_providerItem1Title", // "Body coverage"
  providerItem1Body: "htc_providerItem1Body",
  providerItem2Title: "htc_providerItem2Title", // "Scan protocol"
  providerItem2Body: "htc_providerItem2Body",
  providerItem3Title: "htc_providerItem3Title", // "Imaging biomarkers"
  providerItem3Body: "htc_providerItem3Body",
  providerItem4Title: "htc_providerItem4Title", // "Blood biomarkers"
  providerItem4Body: "htc_providerItem4Body",
  providerItem5Title: "htc_providerItem5Title", // "Report quality"
  providerItem5Body: "htc_providerItem5Body",
  providerItem6Title: "htc_providerItem6Title", // "Clinical facility & team"
  providerItem6Body: "htc_providerItem6Body",
  providerItem7Title: "htc_providerItem7Title", // "Longitudinal tracking"
  providerItem7Body: "htc_providerItem7Body",
  providerItem8Title: "htc_providerItem8Title", // "Data continuity"
  providerItem8Body: "htc_providerItem8Body",

  // CTA
  ctaHeading: "htc_ctaHeading", // "Choose a check-up that gives you a genuine baseline"
  ctaSubtitle: "htc_ctaSubtitle", // "One you can track, understand, and act on…"
  ctaBook: "htc_ctaBook", // "Book check-up"
  ctaTalk: "htc_ctaTalk", // "Talk to our team"
  ctaTrust1: "htc_ctaTrust1", // "Swiss medical expertise"
  ctaTrust2: "htc_ctaTrust2", // "30,000+ MRIs reviewed"
  ctaTrust3: "htc_ctaTrust3", // "12 organ systems assessed with MRI scan"
  ctaTrust4: "htc_ctaTrust4", // "100+ biomarkers analysed"
} as const
